import * as React from 'react';
import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { getLogger } from '@cian/logger/browser';
import { getTelemetry } from '@cian/telemetry/browser';
import { useSelector } from 'react-redux';

import { Map as MapComponent } from '../../components/Map';
import { InfrastructureMapTypes, MapRouteButton, MapRouteOnboarding, Years } from '../../components/Map/parts';
import { ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES } from '../../constants/infrastructure';
import { YANDEX_API_TELEMETRY_KEY } from '../../constants/map';
import { getMapApiKey, getMapApiVersion } from '../../selectors/common';
import { getIsDistanceCalculatingEnabled } from '../../selectors/distanceCalculating';
import { getIsMapServicesVisible } from '../../selectors/mapServices';
import { getGeoCoordinates } from '../../selectors/newbuilding';
import {
  EMapService,
  EPanoramaState,
  IRecommendationNewbuildingInfrastructure,
  TRegularInfrastructureItemType,
} from '../../types/map';
import {
  trackBuildRouteClose,
  trackFullscreenShow,
  trackInfrastructureClick,
  trackInfrastructureShow,
  trackPanoramaShow,
  trackRulerClick,
  trackSimilarNewbuildingInfrastructureOpen,
  trackYearClick,
} from './analytics';
import { getZoom } from './helpers';
import {
  useGeoObjectBalloon,
  useGeoObjects,
  useInfrastructureTypesState,
  useMapOnboardingState,
  useFutureInfrastructure,
  useFutureTypes,
  useSelectedYear,
  useSetGeoObjectsCallbacks,
  useRouting,
  useMapRef,
} from './hooks';
import { toRegularInfrastructureItems } from './helpers/mappers';
import { FutureInfrastructureOnboarding } from './parts/FutureInfrastructureOnboarding';
import { MapServicesContainer } from './parts/MapServices';
import { TransportAccessibilitySidebarContainer } from './parts/TransportAccessibilitySidebar';

export function MapContainer() {
  const pinCoordinates = useSelector(getGeoCoordinates);
  const apiKey = useSelector(getMapApiKey);
  const apiVersion = useSelector(getMapApiVersion);
  const isDistanceCalculatingEnabled = useSelector(getIsDistanceCalculatingEnabled);
  const isMapServicesVisible = useSelector(getIsMapServicesVisible);

  const mapRef = useMapRef();
  const infrastructureTypeRef = React.useRef<string | undefined>();

  const {
    regularInfrastructureByYearRef,
    transportAccessibilityInfrastructureByTypeRef,
    regularInfrastructureTypes,
    handleGeoObjectsFirstFetch,
  } = useGeoObjects();

  const [onboardingOpen, handleCloseOnboarding] = useMapOnboardingState();
  const [currentTypes, setCurrentTypes] = useInfrastructureTypesState(regularInfrastructureTypes);
  const { handleSelectYear, selectedYear, nextAvailableYear } = useSelectedYear(regularInfrastructureByYearRef);
  const [futureTypes, setFutureTypes] = useFutureTypes(regularInfrastructureByYearRef);
  const hasFutureInfrastructure = useFutureInfrastructure(regularInfrastructureByYearRef);

  const {
    handleBalloonRouteButtonClick,
    handleRoutingClick,
    handleRoutingBuildInit,
    handleRouteBuildingSuccess,
    resetTARouting,
  } = useRouting({
    hasFutureInfrastructure,
    handleCloseOnboarding,
    mapApi: mapRef.current,
    regularInfrastructureType: infrastructureTypeRef.current,
  });

  const [isFullscreen, setFullscreenState] = React.useState(false);
  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const [isRouteMode, setRouteMode] = React.useState(false);
  const [panoramaState, setPanoramaState] = React.useState<EPanoramaState | null>(null);
  const [mapService, setMapService] = React.useState<EMapService | null>(null);
  const isPanoramaMode = panoramaState !== null;
  const isRegularInfrastructureMode =
    !isPanoramaMode && !isRouteMode && mapService !== EMapService.TransportAccessibility;
  const isRouteButtonVisible =
    !isMapServicesVisible && isMapLoaded && isRegularInfrastructureMode && isDistanceCalculatingEnabled;
  const yearsTimelineVisible = !isMapServicesVisible && hasFutureInfrastructure; // TODO: Будет удалено при выпиле фича-флага в NB-1153.
  const isMapServiceMenuVisible = isMapServicesVisible && mapService === null;

  const { setRegularGeoObjects, setTransportAccessibilityGeoObjects } = useSetGeoObjectsCallbacks(
    mapRef.current,
    regularInfrastructureByYearRef.current,
    transportAccessibilityInfrastructureByTypeRef.current,
    currentTypes,
    selectedYear,
    setFutureTypes,
    resetTARouting,
  );

  React.useEffect(() => {
    if (isMapLoaded) {
      setRegularGeoObjects(currentTypes, selectedYear, isMapServicesVisible);
    }
  }, [currentTypes, selectedYear, isMapServicesVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSimilarNewbuildingClick = React.useCallback((item: IRecommendationNewbuildingInfrastructure) => {
    trackSimilarNewbuildingInfrastructureOpen(item);
  }, []);

  const { handleBalloonHtmlFirstFetch, handleBalloonOpen } = useGeoObjectBalloon({
    regularInfrastructureByYearRef,
    transportAccessibilityInfrastructureByTypeRef,
    onRouteButtonClick: handleBalloonRouteButtonClick,
    onSimilarNewbuildingClick: handleSimilarNewbuildingClick,
    resetTARouting,
  });

  const handleChangeInfrastructureType = React.useCallback((types: string[]) => {
    setCurrentTypes(types);

    const objectType = types.find(type => !ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES.includes(type));

    const infrastructureItems = toRegularInfrastructureItems(regularInfrastructureByYearRef.current);

    trackInfrastructureClick({ infrastructureItems, objectType });
    infrastructureTypeRef.current = objectType;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFullscreenEnter = React.useCallback(() => {
    setFullscreenState(true);
    trackFullscreenShow(true, infrastructureTypeRef.current);
  }, []);

  const handleFullscreenExit = React.useCallback(() => {
    setFullscreenState(false);
    trackFullscreenShow(false, infrastructureTypeRef.current);
  }, []);

  const handleMapLoaded = React.useCallback(() => setIsMapLoaded(true), []);
  const handleRouteModeEnter = React.useCallback(() => setRouteMode(true), []);

  const handleRouteModeExit = React.useCallback(() => {
    setRouteMode(false);
    trackBuildRouteClose();
  }, []);

  const handlePanoramaSuccessEnter = React.useCallback(() => {
    getTelemetry().increment(YANDEX_API_TELEMETRY_KEY, 5);
    setPanoramaState(EPanoramaState.Loaded);
    trackPanoramaShow(true, infrastructureTypeRef.current);
  }, []);

  const handleNoPanoramaEnter = React.useCallback(() => {
    getTelemetry().increment(YANDEX_API_TELEMETRY_KEY, 5);
    setPanoramaState(EPanoramaState.None);
    trackPanoramaShow(true, infrastructureTypeRef.current);
  }, []);

  const handlePanoramaExit = React.useCallback(() => {
    setPanoramaState(null);
    trackPanoramaShow(false, infrastructureTypeRef.current);
  }, []);

  const handleYearClick = React.useCallback(
    (selectedYear: number) => {
      const selectedInfrastructureType = (infrastructureTypeRef.current || 'metro') as TRegularInfrastructureItemType;
      trackYearClick({ year: selectedYear, objectType: selectedInfrastructureType });
      handleSelectYear(selectedYear);
    },
    [infrastructureTypeRef, handleSelectYear],
  );

  const handleRulerClick = React.useCallback(() => {
    trackRulerClick(infrastructureTypeRef.current);
  }, []);

  const handleError = React.useCallback((error: Error) => {
    getLogger().error(error);
  }, []);

  if (!pinCoordinates) {
    return null;
  }

  return (
    <ActionAfterViewed callback={trackInfrastructureShow} percentVisible={0.5}>
      <MapComponent
        apiKey={apiKey}
        apiVersion={apiVersion}
        mapRef={mapRef}
        isFullscreen={isFullscreen}
        pinCoordinates={pinCoordinates}
        currentTypes={currentTypes}
        zoom={getZoom(currentTypes.length)}
        infrastructureTypes={
          isRegularInfrastructureMode &&
          !!regularInfrastructureTypes && (
            <InfrastructureMapTypes
              types={regularInfrastructureTypes}
              futureTypes={futureTypes}
              currentTypes={currentTypes}
              onTypesChange={handleChangeInfrastructureType}
            />
          )
        }
        years={
          yearsTimelineVisible && (
            <FutureInfrastructureOnboarding>
              <Years
                selectedYear={selectedYear}
                nextAvailableYear={nextAvailableYear}
                onChange={handleYearClick}
                isFullscreen={isFullscreen}
              />
            </FutureInfrastructureOnboarding>
          )
        }
        routeButton={
          isRouteButtonVisible ? (
            <MapRouteOnboarding open={onboardingOpen} onClose={handleCloseOnboarding}>
              <MapRouteButton onClick={handleRoutingClick} />
            </MapRouteOnboarding>
          ) : null
        }
        warningText={hasFutureInfrastructure ? 'Данные могут быть неточными' : undefined}
        mapServices={isMapServiceMenuVisible && <MapServicesContainer onServiceClick={setMapService} />}
        transportAccessibilityService={
          mapService === EMapService.TransportAccessibility && (
            <TransportAccessibilitySidebarContainer
              onTransportAccessibilityChange={setTransportAccessibilityGeoObjects}
              onClose={() => setMapService(null)}
            />
          )
        }
        onGeoObjectsFirstFetch={handleGeoObjectsFirstFetch}
        onBalloonHtmlFirstFetch={handleBalloonHtmlFirstFetch}
        onError={handleError}
        onFullscreenEnter={handleFullscreenEnter}
        onFullscreenExit={handleFullscreenExit}
        onPanoramaSuccessEnter={handlePanoramaSuccessEnter}
        onNoPanoramaEnter={handleNoPanoramaEnter}
        onPanoramaExit={handlePanoramaExit}
        onMapLoaded={handleMapLoaded}
        onBalloonOpen={handleBalloonOpen}
        onRoutingStart={handleRouteModeEnter}
        onRoutingEnd={handleRouteModeExit}
        onRouteBuildingInit={handleRoutingBuildInit}
        onRouteBuildingSuccess={handleRouteBuildingSuccess}
        onRulerClick={handleRulerClick}
      />
    </ActionAfterViewed>
  );
}
